import { useEffect, useRef } from 'react';

import { apiClient } from '@app/app/lib/apiClient';
import { serverPath } from '@app/utils/server-path';
import { route_path } from '@app/utils/route_path';
import { addLanguagePrefix, removeLanguagePrefix } from '@app/app/i18n/utils/language-url';
import i18n from '@app/app/i18n/config';

// Серверная сессия живёт 5 минут — пингуем вдвое чаще, чтобы держать её живой
const SESSION_PING_INTERVAL_MS = 150_000; // 2.5 минуты

/**
 * Нормализованная ошибка apiClient: response-интерсептор кладёт статус в `_metadata`.
 * `statusCode` — число для HTTP-ответа (напр. 401), `null` для сетевой ошибки/таймаута.
 */
type NormalizedApiError = { _metadata?: { statusCode?: number | null } };

const getStatusCode = (error: unknown): number | null => {
  const statusCode = (error as NormalizedApiError)?._metadata?.statusCode;
  return typeof statusCode === 'number' ? statusCode : null;
};

/**
 * Сегменты auth-страниц — их нельзя класть в `next`, иначе после входа пользователя
 * может сразу выкинуть (исторический баг: `next=/logout/` → логаут после логина).
 */
const AUTH_PATH_SEGMENTS = ['login', 'register', 'logout', 'password-reset'];

const isAuthPath = (pathname: string): boolean => {
  const firstSegment = removeLanguagePrefix(pathname).split('/').filter(Boolean)[0];
  return AUTH_PATH_SEGMENTS.includes(firstSegment);
};

/**
 * Уводит на страницу логина с backurl в текущий воркспейс.
 * После входа login-страница читает `?next` и возвращает пользователя обратно.
 * Хард-редирект (а не SPA-navigate): серверная сессия уже мертва, свежая загрузка
 * сбрасывает клиентский auth-стейт и совпадает с тем, как сам логин делает возврат.
 *
 * `next` НЕ добавляем, если текущий путь — auth-страница (login/logout/register/
 * password-reset): интервал живёт сквозь навигацию и мог сработать уже на `/logout`.
 * Тогда уводим на голый логин. Login-страница дополнительно фильтрует `next` через
 * свой `forbiddenRoutes` — это второй рубеж защиты (defense in depth).
 */
const redirectToLogin = (): void => {
  const currentPath = window.location.pathname; // текущий путь (воркспейс) как backurl
  const loginPath = addLanguagePrefix(route_path.login, i18n.language);
  const target = isAuthPath(currentPath)
    ? loginPath
    : `${loginPath}?next=${encodeURIComponent(currentPath)}`;
  window.location.assign(target);
};

/**
 * Держит серверную сессию живой (конкурентные лицензии для СИБУР, SLIDES-3935).
 *
 * Пока `enabled`, периодически шлёт лёгкий GET на `/session-ping/` с интервалом
 * вдвое меньше времени жизни сессии.
 *
 * Реакция на разлогин: реагируем строго на `401` (сессия истекла после сна вкладки/ПК) —
 * уводим на логин с backurl обратно в воркспейс. `200` — ок; любой другой код и сетевые
 * ошибки/таймауты — поведение не определено, ничего не делаем (решение команды: без додумываний).
 *
 * Короткий (5-минутный) TTL сессии существует только на boxed-деплое СИБУРа
 * (backend gate `CONCURRENT_LICENSES_ENABLED`), поэтому вызывать хук нужно
 * с учётом boxed-режима и авторизации.
 *
 * @param enabled - запускать ли keep-alive; при `false` пинг не шлётся
 */
export const useSessionPing = (enabled: boolean): void => {
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRedirectingRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const stop = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };

    const ping = async () => {
      if (isRedirectingRef.current) return;
      try {
        await apiClient.get(serverPath.sessionPing);
      } catch (error) {
        // Реагируем строго на 401 (сессия мертва) → на логин. Любой другой код и
        // сетевые ошибки — поведение не определено, ничего не делаем.
        if (getStatusCode(error) !== 401) return;

        isRedirectingRef.current = true;
        stop();
        redirectToLogin();
      }
    };

    ping();
    intervalRef.current = setInterval(ping, SESSION_PING_INTERVAL_MS);

    return stop;
  }, [enabled]);
};
